import { useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useUsers, useCreateUser, useUpdateUser, useDeleteUser } from "@/hooks/use-users";
import { UserForm } from "@/components/users/user-form";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { parseRoles } from "@/lib/roles";

const ROLE_LABELS: Record<string, string> = {
  admin: "Administrator",
  manager: "Menadžer",
  viewer: "Pregledač",
  veleprodaja: "Veleprodaja",
};

const ROLE_VARIANTS: Record<string, "default" | "secondary" | "outline"> = {
  admin: "default",
  manager: "secondary",
  viewer: "outline",
  veleprodaja: "secondary",
};

export function UsersSection() {
  const { data, isLoading } = useUsers();
  const createUser = useCreateUser();
  const updateUser = useUpdateUser();
  const deleteUser = useDeleteUser();

  const [createOpen, setCreateOpen] = useState(false);
  const [editUser, setEditUser] = useState<any | null>(null);

  const users = data?.users ?? data ?? [];

  async function handleCreate(values: any) {
    try {
      await createUser.mutateAsync(values);
      toast.success("Korisnik kreiran");
      setCreateOpen(false);
    } catch (e: any) {
      toast.error(e.message || "Greška pri kreiranju korisnika");
    }
  }

  async function handleUpdate(values: any) {
    if (!editUser) return;
    try {
      const payload: any = { id: editUser.id, name: values.name, email: values.email, roles: values.roles };
      if (values.password) payload.password = values.password;
      await updateUser.mutateAsync(payload);
      toast.success("Korisnik ažuriran");
      setEditUser(null);
    } catch (e: any) {
      toast.error(e.message || "Greška pri ažuriranju korisnika");
    }
  }

  async function handleDelete(id: string, name: string) {
    if (!confirm(`Jeste li sigurni da želite obrisati korisnika "${name}"?`)) return;
    try {
      await deleteUser.mutateAsync(id);
      toast.success("Korisnik obrisan");
    } catch (e: any) {
      toast.error(e.message || "Greška pri brisanju");
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-end">
        <Button size="sm" onClick={() => setCreateOpen(true)}>Novi korisnik</Button>
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : (
        <div className="ring-1 ring-foreground/10">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="p-2 text-left font-medium text-xs">Ime</th>
                <th className="p-2 text-left font-medium text-xs">Email</th>
                <th className="p-2 text-left font-medium text-xs">Uloga</th>
                <th className="p-2 text-right font-medium text-xs">Akcije</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u: any) => (
                <tr key={u.id} className="border-b hover:bg-muted/50 transition-colors">
                  <td className="p-2 font-medium">{u.name}</td>
                  <td className="p-2 text-muted-foreground">{u.email}</td>
                  <td className="p-2">
                    <div className="flex flex-wrap gap-1">
                      {parseRoles(u).map((role) => (
                        <Badge key={role} variant={ROLE_VARIANTS[role] ?? "outline"}>
                          {ROLE_LABELS[role] ?? role}
                        </Badge>
                      ))}
                    </div>
                  </td>
                  <td className="p-2">
                    <div className="flex gap-1 justify-end">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setEditUser(u)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(u.id, u.name)}
                        disabled={deleteUser.isPending}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={4} className="p-8 text-center text-muted-foreground">
                    Nema korisnika
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <UserForm
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSubmit={handleCreate}
        loading={createUser.isPending}
      />

      {editUser && (
        <UserForm
          open={!!editUser}
          onOpenChange={(open) => { if (!open) setEditUser(null); }}
          initialValues={editUser}
          onSubmit={handleUpdate}
          loading={updateUser.isPending}
          isEdit
        />
      )}
    </div>
  );
}
